import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getToken } from "@/utils/auth";
import ProtectedRoute from "@/components/ProtectedRoute";
import RoleGate from "@/components/RoleGate";
import Lucide from "@/components/Base/Lucide";
import { Menu, Popover } from "@/components/Base/Headless";
import Pagination from "@/components/Base/Pagination";
import { FormCheck, FormInput, FormSelect } from "@/components/Base/Form";
import Tippy from "@/components/Base/Tippy";
// import humans from "@/fakers/users"; // Disabled faker profiles
import Button from "@/components/Base/Button";
import Table from "@/components/Base/Table";
import clsx from "clsx";
import _ from "lodash";
import { Edit } from "lucide-react";
import { useAppDispatch } from "@/stores/hooks";
import { setUserId } from "@/stores/sideMenuSlice";

type User = {
    id: string;
    firstName: string;
    lastName: string;
    username: string;
    mobileNo?: string;
    role?: { role: string };
    status: string;
}
 function UsersPage() {
    const [users, setUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState(true);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchQuery, setSearchQuery] = useState("");
    const [pageSize, setPageSize] = useState(20);
    const [searchInput, setSearchInput] = useState("");

    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    useEffect(() => {
        (async () => {
            try {
                setLoading(true);
                const companyId = new URLSearchParams(window.location.search).get("companyId");

                const queryParams = new URLSearchParams();
                if (companyId) queryParams.append("companyId", companyId);
                if (searchQuery) queryParams.append("q", searchQuery);
                queryParams.append("page", currentPage.toString());
                queryParams.append("pageSize", pageSize.toString());

                const res = await fetch(
                    `${import.meta.env.VITE_API_URL}/users?${queryParams.toString()}`,
                    {
                        headers: { Authorization: `Bearer ${getToken()}` }
                    }
                );
                const data = await res.json();
                setUsers(Array.isArray(data?.data) ? data.data : []);
                setTotalPages(data.totalPages || 1);
                setTotal(data.total || 0);

            } catch (err) {
                console.error("Failed to fetch users:", err);
            } finally {
                setLoading(false);
            }
        })();
    }, [currentPage, searchQuery, pageSize]);

    const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setSearchQuery(searchInput);
        setCurrentPage(1);
    };

    const handleDelete = async (id: string) => {
        if (!confirm("Are you sure you want to delete this user?")) return;

        try {
            const res = await fetch(` ${import.meta.env.VITE_API_URL}/users/${id}`, {
                method: 'DELETE',
                headers: {
                    Authorization: `Bearer ${getToken()}`
                }
            });

            if (res.ok) {
                setUsers(prev => prev.filter(c => c.id !== id));
            } else {
                const error = await res.json();
                alert(`Failed to delete user: ${error?.error || "Unknown error"}`);
            }
        } catch (err) {
            console.error("Delete failed", err);
            alert("Something went wrong deleting user");
        }
    };

    return (
        <ProtectedRoute>
            <RoleGate allow={["SUPERADMIN", "ADMIN"]}>
                <div className="grid grid-cols-12 gap-y-10 gap-x-6">
                    <div className="col-span-12">
                        <div className="flex flex-col md:h-10 gap-y-3 md:items-center md:flex-row">
                            <div className="text-base font-medium group-[.mode--light]:text-white">
                                List of users
                            </div>
                            <div className="flex flex-col sm:flex-row gap-x-3 gap-y-2 md:ml-auto">
                                <Button
                                    variant="primary"
                                    className="group-[.mode--light]:!bg-white/[0.12] group-[.mode--light]:!text-slate-200 group-[.mode--light]:!border-transparent dark:group-[.mode--light]:!bg-darkmode-900/30 dark:!box"
                                    onClick={() => {
                                        const searchParams = new URLSearchParams(window.location.search);
                                        const companyId = searchParams.get("companyId");
                                        const projectId = searchParams.get("projectId");
                                        
                                        let addUrl = "/users/add";
                                        if (projectId) {
                                            addUrl += `?projectId=${projectId}`;
                                        } else if (companyId) {
                                            addUrl += `?companyId=${companyId}`;
                                        }
                                        
                                        navigate(addUrl);
                                    }}
                                >
                                    <Lucide icon="PenLine" className="stroke-[1.3] w-4 h-4 mr-2" />{" "}
                                    Add New User
                                </Button>
                                
                                {/* New buttons for Employee Category, Company Department */}
                                <Button
                                    variant="outline-secondary"
                                    className="group-[.mode--light]:!bg-white/[0.12] group-[.mode--light]:!text-slate-200 group-[.mode--light]:!border-transparent dark:group-[.mode--light]:!bg-darkmode-900/30 dark:!box"
                                    onClick={() => {
                                        const searchParams = new URLSearchParams(window.location.search);
                                        const companyId = searchParams.get("companyId");
                                        const projectId = searchParams.get("projectId");
                                        
                                        let categoryUrl = "/employee-categories";
                                        if (projectId) {
                                            categoryUrl += `?projectId=${projectId}`;
                                        } else if (companyId) {
                                            categoryUrl += `?companyId=${companyId}`;
                                        }
                                        
                                        navigate(categoryUrl);
                                    }}
                                >
                                    <Lucide icon="Package" className="stroke-[1.3] w-4 h-4 mr-2" />{" "}
                                    Employee Category
                                </Button>

                                <Button
                                    variant="outline-secondary"
                                    className="group-[.mode--light]:!bg-white/[0.12] group-[.mode--light]:!text-slate-200 group-[.mode--light]:!border-transparent dark:group-[.mode--light]:!bg-darkmode-900/30 dark:!box"
                                    onClick={() => {
                                        const searchParams = new URLSearchParams(window.location.search);
                                        const companyId = searchParams.get("companyId");
                                        const projectId = searchParams.get("projectId");
                                        
                                        let deptUrl = "/co-departments";
                                        if (projectId) {
                                            deptUrl += `?projectId=${projectId}`;
                                        } else if (companyId) {
                                            deptUrl += `?companyId=${companyId}`;
                                        }
                                        
                                        navigate(deptUrl);
                                    }}
                                >
                                    <Lucide icon="Tag" className="stroke-[1.3] w-4 h-4 mr-2" />{" "}
                                    Company Department
                                </Button>

                            </div>
                        </div>
                        <div className="flex flex-col gap-8 mt-3.5">
                            <div className="flex flex-col box box--stacked">
                                <div className="flex flex-col p-5 sm:items-center sm:flex-row gap-y-2">
                                    <div>
                                        <form onSubmit={handleSearch}>
                                            <div className="relative">
                                                <Lucide
                                                    icon="Search"
                                                    className="absolute inset-y-0 left-0 z-10 w-4 h-4 my-auto ml-3 stroke-[1.3] text-slate-500"
                                                />
                                                <FormInput
                                                    type="text"
                                                    placeholder="Search users..."
                                                    className="pl-9 sm:w-64 rounded-[0.5rem]"
                                                    value={searchInput}
                                                    onChange={(e) => setSearchInput(e.target.value)}
                                                />
                                            </div>
                                        </form>
                                    </div>
                                    <div className="flex flex-col sm:flex-row gap-x-3 gap-y-2 sm:ml-auto">
                                        <Menu>
                                            <Menu.Button
                                                as={Button}
                                                variant="outline-secondary"
                                                className="w-full sm:w-auto"
                                            >
                                                <Lucide
                                                    icon="Download"
                                                    className="stroke-[1.3] w-4 h-4 mr-2"
                                                />
                                                Export
                                                <Lucide
                                                    icon="ChevronDown"
                                                    className="stroke-[1.3] w-4 h-4 ml-2"
                                                />
                                            </Menu.Button>
                                            <Menu.Items className="w-40">
                                                <Menu.Item>
                                                    <Lucide icon="FileBarChart" className="w-4 h-4 mr-2" />{" "}
                                                    PDF
                                                </Menu.Item>
                                                <Menu.Item>
                                                    <Lucide icon="FileBarChart" className="w-4 h-4 mr-2" />
                                                    CSV
                                                </Menu.Item>
                                            </Menu.Items>
                                        </Menu>
                                        <Popover className="inline-block">
                                            {({ close }) => (
                                                <>
                                                    <Popover.Button
                                                        as={Button}
                                                        variant="outline-secondary"
                                                        className="w-full sm:w-auto"
                                                    >
                                                        <Lucide
                                                            icon="ArrowDownWideNarrow"
                                                            className="stroke-[1.3] w-4 h-4 mr-2"
                                                        />
                                                        Filter
                                                        <div className="flex items-center justify-center h-5 px-1.5 ml-2 text-xs font-medium border rounded-full bg-slate-100 dark:bg-darkmode-400">
                                                            3
                                                        </div>
                                                    </Popover.Button>
                                                    <Popover.Panel placement="bottom-end">
                                                        <div className="p-2">
                                                            <div>
                                                                <div className="text-left text-slate-500">
                                                                    Position
                                                                </div>
                                                                <FormSelect className="flex-1 mt-2">
                                                                    <option value="">All Positions</option>
                                                                    {/* Faker profiles disabled - using empty filter */}
                                                                </FormSelect>
                                                            </div>
                                                            <div className="mt-3">
                                                                <div className="text-left text-slate-500">
                                                                    Department
                                                                </div>
                                                                <FormSelect className="flex-1 mt-2">
                                                                    <option value="">All Departments</option>
                                                                    {/* Faker profiles disabled - using empty filter */}
                                                                </FormSelect>
                                                            </div>
                                                            <div className="flex items-center mt-4">
                                                                <Button
                                                                    variant="secondary"
                                                                    onClick={() => {
                                                                        close();
                                                                    }}
                                                                    className="w-32 ml-auto"
                                                                >
                                                                    Close
                                                                </Button>
                                                                <Button variant="primary" className="w-32 ml-2">
                                                                    Apply
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    </Popover.Panel>
                                                </>
                                            )}
                                        </Popover>
                                    </div>
                                </div>
                                <div className="overflow-auto xl:overflow-visible">
                                    <Table className="border-b border-slate-200/60 text-xs">
                                        <Table.Thead className="text-xs">
                                            <Table.Tr>
                                                <Table.Td className="py-1.5">First Name</Table.Td>
                                                <Table.Td className="py-1.5">Last Name</Table.Td>
                                                <Table.Td className="py-1.5">Username</Table.Td>
                                                <Table.Td className="py-1.5">Mobile No</Table.Td>
                                                <Table.Td className="py-1.5">Role</Table.Td>
                                                <Table.Td className="py-1.5">Status</Table.Td>
                                                <Table.Td className="text-right py-1.5">Action</Table.Td>
                                            </Table.Tr>
                                        </Table.Thead>
                                        <Table.Tbody>
                                            {users.map((user) => (
                                                <Table.Tr key={user.id} className="[&_td]:last:border-b-0">
                                                    <Table.Td className="py-1.5 border-dashed dark:bg-darkmode-600 text-xs">
                                                        <Link
                                                            to={`/user?userId=${user.id}`}
                                                            onClick={() => dispatch(setUserId(user.id))}
                                                            className="text-primary hover:underline"
                                                            title="View Projects"
                                                        >
                                                            {user.firstName}
                                                        </Link>
                                                    </Table.Td>
                                                    <Table.Td className="py-1.5 border-dashed dark:bg-darkmode-600 text-xs">
                                                        {user.lastName}
                                                    </Table.Td>
                                                    <Table.Td className="py-1.5 border-dashed dark:bg-darkmode-600 text-xs">
                                                        {user.username}
                                                    </Table.Td>
                                                    <Table.Td className="py-1.5 border-dashed dark:bg-darkmode-600 text-xs">
                                                        {user.mobileNo}
                                                    </Table.Td>
                                                    <Table.Td className="py-1.5 border-dashed dark:bg-darkmode-600 text-xs">
                                                        {user.role?.role || "—"}
                                                    </Table.Td>
                                                    <Table.Td className="py-1.5 border-dashed dark:bg-darkmode-600 text-xs">
                                                        {user.status ? "Active" : "Inactive"}
                                                    </Table.Td>
                                                    <Table.Td className="py-1.5 text-right border-dashed dark:bg-darkmode-600 text-xs">
                                                        <div className="flex justify-end items-center gap-3">
                                                            {/* Edit */}
                                                            <Link
                                                                to={(() => {
                                                                    const searchParams = new URLSearchParams(window.location.search);
                                                                    const companyId = searchParams.get("companyId");
                                                                    const projectId = searchParams.get("projectId");
                                                                    
                                                                    let editUrl = `/users/${user.id}/edit`;
                                                                    if (projectId) {
                                                                        editUrl += `?projectId=${projectId}`;
                                                                    } else if (companyId) {
                                                                        editUrl += `?companyId=${companyId}`;
                                                                    }
                                                                    
                                                                    return editUrl;
                                                                })()}
                                                                className="btn btn-outline-secondary p-2 rounded-lg"
                                                                title="Edit"
                                                            >
                                                                <Lucide icon="Pencil" className="w-3 h-3" />
                                                            </Link>

                                                            {/* Delete (unchanged) */}
                                                            <button
                                                                onClick={() => handleDelete(user.id)}
                                                                className="btn btn-outline-danger p-2 rounded-lg"
                                                                title="Delete"
                                                            >
                                                                <Lucide icon="Trash2" className="w-3 h-3" />
                                                            </button>
                                                        </div>
                                                    </Table.Td>
                                                </Table.Tr>
                                            ))}
                                        </Table.Tbody>

                                    </Table>
                                </div>
                                <div className="flex flex-col-reverse flex-wrap items-center p-5 flex-reverse gap-y-2 sm:flex-row">
                                    <div className="text-sm text-slate-500 mr-4">
                                        {total > 0 ? (
                                            <>
                                                Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, total)} of {total} users
                                            </>
                                        ) : (
                                            "No users found"
                                        )}
                                    </div>
                                    <Pagination className="flex-1 w-full mr-auto sm:w-auto">
                                        {currentPage > 1 && (
                                            <Pagination.Link onClick={() => setCurrentPage(1)}>
                                                <Lucide icon="ChevronsLeft" className="w-4 h-4" />
                                            </Pagination.Link>
                                        )}
                                        {currentPage > 1 && (
                                            <Pagination.Link onClick={() => setCurrentPage(currentPage - 1)}>
                                                <Lucide icon="ChevronLeft" className="w-4 h-4" />
                                            </Pagination.Link>
                                        )}
                                        {totalPages > 7 && currentPage > 4 && (
                                            <>
                                                <Pagination.Link onClick={() => setCurrentPage(1)}>1</Pagination.Link>
                                                {currentPage > 5 && <Pagination.Link>...</Pagination.Link>}
                                            </>
                                        )}
                                        {Array.from({ length: totalPages }).map((_, i) => {
                                            const pageNum = i + 1;
                                            // Show first 3 pages, last 3 pages, and pages around current page
                                            if (
                                                totalPages <= 7 ||
                                                pageNum <= 3 ||
                                                pageNum > totalPages - 3 ||
                                                (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                                            ) {
                                                return (
                                                    <Pagination.Link
                                                        key={i}
                                                        active={currentPage === pageNum}
                                                        onClick={() => setCurrentPage(pageNum)}
                                                    >
                                                        {pageNum}
                                                    </Pagination.Link>
                                                );
                                            }
                                            return null;
                                        })}
                                        {totalPages > 7 && currentPage < totalPages - 3 && (
                                            <>
                                                {currentPage < totalPages - 4 && <Pagination.Link>...</Pagination.Link>}
                                                <Pagination.Link onClick={() => setCurrentPage(totalPages)}>
                                                    {totalPages}
                                                </Pagination.Link>
                                            </>
                                        )}
                                        {currentPage < totalPages && (
                                            <Pagination.Link onClick={() => setCurrentPage(currentPage + 1)}>
                                                <Lucide icon="ChevronRight" className="w-4 h-4" />
                                            </Pagination.Link>
                                        )}
                                        {currentPage < totalPages && (
                                            <Pagination.Link onClick={() => setCurrentPage(totalPages)}>
                                                <Lucide icon="ChevronsRight" className="w-4 h-4" />
                                            </Pagination.Link>
                                        )}
                                    </Pagination>
                                    <FormSelect 
                                        className="sm:w-20 rounded-[0.5rem]"
                                        value={pageSize}
                                        onChange={(e) => {
                                            setPageSize(Number(e.target.value));
                                            setCurrentPage(1);
                                        }}
                                    >
                                        <option value={10}>10</option>
                                        <option value={25}>25</option>
                                        <option value={50}>50</option>
                                        <option value={100}>100</option>
                                        <option value={250}>250</option>
                                        <option value={500}>500</option>
                                    </FormSelect>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </RoleGate>
        </ProtectedRoute>
     );
}

export default UsersPage;